import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/api/client";
import { resetUnauthorizedState, setUnauthorizedHandler } from "@/lib/queryClient";

export interface User {
  id: string;
  org_id: string;
  email: string;
  name: string;
  role: string;
  org_name: string;
  org_slug: string;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  refreshAuth: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  refreshAuth: async () => {},
  logout: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshAuth = useCallback(async () => {
    try {
      const u = await api<User>("/api/v2/identity/me");
      setUser(u);
      resetUnauthorizedState();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await api("/api/v2/auth/logout", { method: "POST" });
    setUser(null);
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  // Single-flight 401 handler (D1): any TanStack Query hook that receives a
  // 401 clears the cache and drops the user; ProtectedRoute already
  // redirects to /login when `user` becomes null, so no imperative
  // navigation is needed here.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refreshAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
